import React, { useEffect, useState as useStateC } from 'react';
import bemCn from 'bem-cn';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { RouteFormatter } from '../../routes';
import { BLOG_AUTH_DEF_POSTS, BLOG_LIMIT_POSTS } from '../../consts/blog.consts';
import BlogArticleDesktopLow from '../../components/blog-article-desktop-low';
import BlogArticleSimple from '../../components/blog-article-simple';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import BlogCategories from '../../components/blog-categories';
import BlogSideBar from '../../components/blog-side-bar';
import SniffButton from '../../components/sniff-button';
import defSpotImg from '../../assets/img/home/carousel-4.jpg?sizes[]=450,sizes[]=600';
import BlogTopNav from '../../components/blog-top-nav';
import BlogSocial from '../../components/blog-social';
import BlogFooter from '../../components/blog-footer';
import PictureSet from '../../components/picture-set';
import FooterNew from '../../components/footer-new';
import Preloader from '../../components/preloader';
import NotFound from '../not-found';
import './author.scss';

const router = require('react-router');
const hook = require('../../services/hooks/useGetBlogAuthor');
const useGetBlogPosts = require('../../services/hooks/useGetBlogPosts');

const b = bemCn('sniff-blog-author');

const getListBread = (data) =>
    data ? [{ name: 'Blog', url: RouteFormatter.blog({}) }, { name: data.name }] : [{ name: 'Blog', url: RouteFormatter.blog({}) }];

const BlogAuthor = ({ blogAuthorSSR, blogCategoriesSSR, useGetBlogPostsSSR, isMobileSSR, useStateSSR }) => {
    const isMobile = useStateSSR ? isMobileSSR : useMobileScreen();
    const { slug } = !useStateSSR && router.useParams();
    const { data, loading: loadingPage } = !useStateSSR && hook.useGetBlogAuthor(slug);
    const blogAuthor = blogAuthorSSR || data?.blogAuthor;
    const blogCategories = blogCategoriesSSR || data?.blogCategories;
    const { getBlogPosts, loading } = useStateSSR ? (useGetBlogPostsSSR ? useGetBlogPostsSSR() : {}) : useGetBlogPosts.default();
    const useState = useStateSSR || useStateC;
    const listBread = getListBread(blogAuthor);
    const [limit, setLimit] = useState(BLOG_AUTH_DEF_POSTS);
    const [recentPosts, setRecentPosts] = useState(blogAuthor?.recentPosts || []);
    const [maxPost, setMaxPost] = useState(recentPosts ? recentPosts?.length < limit : false);
    const authorPhoto = blogAuthor?.photoDetailed
        ? ImageHelper.getUrlFromOptions(blogAuthor.photoDetailed, `fit-in/${IMG_RESIZES.x0450}`)
        : defSpotImg;

    const loadMore = async () => {
        const newLimit = limit + BLOG_LIMIT_POSTS;
        const authorSlug = blogAuthor.slug;

        try {
            const res = await getBlogPosts({ variables: { authorSlug, page: 1, limit: newLimit } });
            const newPosts = res.data.blogPosts;
            if (newPosts.length < newLimit) {
                setMaxPost(true);
            }
            setRecentPosts([...newPosts]);
            setLimit(newLimit);
        } catch (error) {
            console.warn(error);
        }
    };

    useEffect(() => {
        if (data?.blogAuthor?.recentPosts) {
            setRecentPosts(data?.blogAuthor?.recentPosts);
            setMaxPost(data?.blogAuthor?.recentPosts?.length < limit);
        }
    }, [data?.blogAuthor]);

    if (!blogAuthor && !loadingPage) {
        return <NotFound />;
    } else if (!blogAuthor || loadingPage) {
        return <Preloader fullWindow />;
    }

    return (
        <div className={b()}>
            <BlogTopNav />
            <div className="d-none d-md-block b-bot mb-3" />
            <div className="desktop-container">
                <div className="mx-2 mx-md-0">
                    <SpotBreadcrumbs {...{ listBread, SSR: !!useStateSSR }} />
                </div>
            </div>
            <div className={b('content').mix('desktop-container')}>
                <div className="mx-2 mx-md-0">
                    <div className={b('aut-details')}>
                        <PictureSet
                            className={b('thumb')}
                            imgs={authorPhoto}
                            width={400}
                            height={400}
                            useState={useStateSSR}
                            SSR={!!useStateSSR}
                            alt={blogAuthor.name + ' photo'}
                            method="eager"
                        />
                        <div>
                            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc">{blogAuthor.name}</h1>
                            <p className="snif-p mt-1">{blogAuthor.title}</p>
                            <BlogSocial {...{ blogAuthor }} />
                        </div>
                    </div>
                    <div className={b('desc')} dangerouslySetInnerHTML={{ __html: blogAuthor?.description }} />
                    {blogAuthor?.education && (
                        <div className="mt-5">
                            <h2 className="snif-m2 snif-l2-pc snif-semibold mb-2">Education</h2>
                            <div className={b('aut-desc')} dangerouslySetInnerHTML={{ __html: blogAuthor?.education }} />
                        </div>
                    )}
                    {blogAuthor?.featured && (
                        <div className="mt-5">
                            <h2 className="snif-m2 snif-l2-pc snif-semibold mb-2">Proudly featured in...</h2>
                            <div className={b('aut-desc')} dangerouslySetInnerHTML={{ __html: blogAuthor?.featured }} />
                        </div>
                    )}
                </div>
                <BlogSideBar {...{ useStateSSR }} />
            </div>
            <div className="desktop-container mt-5">
                <div className="mx-2 mx-md-0">
                    <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5">Most recent articles</h2>
                    <div className={b('recent')}>
                        {recentPosts.map((v) =>
                            isMobile ? (
                                <BlogArticleSimple key={v.id} data={v} {...{ useStateSSR }} />
                            ) : (
                                <BlogArticleDesktopLow key={v.id} data={v} {...{ useStateSSR }} />
                            )
                        )}
                    </div>
                    {!maxPost && (
                        <p className="text-center mb-5 pt-3 pb-2 pb-md-5">
                            <SniffButton size="lg" color="secondary" onClick={loadMore} disabled={loading}>
                                Load older articles
                            </SniffButton>
                        </p>
                    )}
                    <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5 pt-3 pt-md-0">Top dog guides per area</h2>
                    <BlogCategories categories={blogCategories} />
                </div>
            </div>
            <BlogFooter {...{ isMobile, useStateSSR }} />
            {!useStateSSR && <FooterNew isHome />}
        </div>
    );
};

export default BlogAuthor;
